import { useState, useMemo } from 'react';
import { notesApiService } from '../services/notesApi';
import { X, Loader, CheckCircle, Axe, HelpCircle, AlertCircle, AlertTriangle } from 'lucide-react';
import { Identity } from '../types/auth';
import { CaseLinkField } from './logbook/CaseLinkField';
import { linkRecordToCase } from '../utils/logbookCaseLink';
import type { CaseChoice } from './logbook/CaseChooser';

interface CreateBanModalProps {
  playerUuid: string;
  user: Identity | null;
  onClose: () => void;
  onSuccess: () => void;
}

type Scope = 'global' | 'cluster' | 'fqdn' | 'universal';

export const CreateBanModal = ({ playerUuid, user, onClose, onSuccess }: CreateBanModalProps) => {
  const [reason, setReason] = useState('');
  const [expires, setExpires] = useState<number | null>(null);
  const [appealable, setAppealable] = useState(true);
  const [scope, setScope] = useState<Scope>('global');
  const [scopeTarget, setScopeTarget] = useState('');
  const [caseChoice, setCaseChoice] = useState<CaseChoice | null>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  /** The ban went through but its case did not. Said plainly rather than folded into the error. */
  const [caseWarning, setCaseWarning] = useState<string | null>(null);
  const [linkedCase, setLinkedCase] = useState<string | null>(null);

  const durationSteps = useMemo(() => [
    { label: '1 Day', seconds: 86400 },
    { label: '3 Days', seconds: 259200 },
    { label: '1 Week', seconds: 604800 },
    { label: '2 Weeks', seconds: 1209600 },
    { label: '1 Month', seconds: 2592000 },
    { label: '3 Months', seconds: 7776000 },
    { label: '6 Months', seconds: 15552000 },
    { label: 'Permanent', seconds: null },
  ], []);

  const [durationIndex, setDurationIndex] = useState(durationSteps.length - 1);

  const userGroups = useMemo(() => new Set(user?.raw?.custom?.groups || []), [user]);

  const permissions = useMemo(() => ({
    isHelper: userGroups.has('SPY_Bans_Helper'),
    isMod: userGroups.has('SPY_Bans_Mod'),
    isAdmin: userGroups.has('SPY_Bans_Admin'),
    isSuperAdmin: userGroups.has('SPY_Bans_Superadmins'),
  }), [userGroups]);

  const allowedScopes = useMemo((): Scope[] => {
    if (permissions.isSuperAdmin) return ['global', 'cluster', 'fqdn', 'universal'];
    if (permissions.isAdmin || permissions.isMod) return ['global', 'cluster', 'fqdn'];
    if (permissions.isHelper) return ['global', 'cluster', 'fqdn'];
    return [];
  }, [permissions]);

  const validateRequest = (): string | null => {
    if (!reason.trim()) return "Reason cannot be empty.";
    
    // No validation needed for super admins or admins
    if (permissions.isSuperAdmin || permissions.isAdmin) {
      return null;
    }

    // Mods can't do universal bans, but have no other restrictions from this form
    if (permissions.isMod) {
       if (scope === 'universal') return "You do not have permission for universal scope.";
      return null;
    }
    
    if (permissions.isHelper) {
      if (scope === 'universal') return "You do not have permission for universal scope.";
      if (scope === 'global') {
        if (!expires) return "Helpers can only issue temporary global bans.";
        const oneMonth = 30 * 24 * 60 * 60;
        if (expires > Date.now() / 1000 + oneMonth) {
          return "Helpers cannot issue global bans longer than one month.";
        }
      }
      if ((scope === 'cluster' || scope === 'fqdn') && expires !== null) {
        return "As a Helper, you can only issue permanent cluster/fqdn bans.";
      }
    }
    
    return null;
  };

  const handleDurationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const index = parseInt(e.target.value, 10);
    setDurationIndex(index);
    
    const selectedStep = durationSteps[index];
    if (selectedStep.seconds === null) {
        setExpires(null);
    } else {
        const newExpires = Math.floor(Date.now() / 1000) + selectedStep.seconds;
        setExpires(newExpires);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const validationError = validateRequest();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setCaseWarning(null);
    setIsSuccess(false);

    try {
      const ban = await notesApiService.createBan({
        user_uuid: playerUuid,
        reason,
        expires,
        appealable: appealable ? 1 : 0,
        scope,
        scope_target: (scope === 'cluster' || scope === 'fqdn') ? scopeTarget : null,
      });

      // The ban is issued from here on. A case that cannot be attached is reported as exactly that,
      // and never as a failed ban: the moderator would go and issue it a second time.
      let linked: string | null = null;
      let failed: string | null = null;
      if (caseChoice) {
        try {
          const target = await linkRecordToCase(caseChoice, 'ban', ban.ban_uuid, playerUuid);
          linked = target.reference;
        } catch {
          failed = 'The ban was issued, but it could not be attached to the case. Attach it from the ban itself.';
        }
      }

      setLinkedCase(linked);
      setCaseWarning(failed);
      setIsSuccess(true);
      onSuccess();
      // The case outcome is worth reading before the modal closes itself.
      setTimeout(onClose, linked || failed ? 2500 : 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay z-50">
      <form onSubmit={handleSubmit} className="modal-panel max-w-2xl">
        <button type="button" onClick={onClose} className="modal-close" title="Close" aria-label="Close"><X className="w-4 h-4" /></button>
        <div className="modal-body">
          <div className="flex flex-col items-center text-center mb-5">
            <span className="icon-tile icon-tile-lg icon-tile-danger mb-3">
              <Axe className="w-5 h-5" />
            </span>
            <h2 className="modal-title mb-1">Ban Player</h2>
            <p className="text-sm text-glass-400">UUID: <span className="font-mono text-xs text-white">{playerUuid}</span></p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Left Column */}
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-glass-400 mb-1.5">Reason</label>
                <textarea value={reason} onChange={e => setReason(e.target.value)} className="form-input min-h-[6rem]" required />
              </div>
               <div>
                <label className="block text-xs font-medium text-glass-400 mb-1.5">
                  Duration: <span className="font-semibold text-white">{durationSteps[durationIndex].label}</span>
                </label>
                <input 
                  type="range" 
                  min="0"
                  max={durationSteps.length - 1}
                  value={durationIndex}
                  onChange={handleDurationChange}
                  className="w-full h-2 bg-glass-200/20 rounded-lg appearance-none cursor-pointer"
                  aria-label="Ban duration"
                />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={appealable} onChange={e => setAppealable(e.target.checked)} id="appealable" className="form-checkbox" />
                <label htmlFor="appealable" className="text-sm text-glass-300 cursor-pointer">Appealable</label>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-glass-400 mb-1.5">Scope</label>
                <select value={scope} onChange={e => setScope(e.target.value as Scope)} className="form-input">
                  {allowedScopes.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              {(scope === 'cluster' || scope === 'fqdn') && (
                <div>
                  <label className="block text-xs font-medium text-glass-400 mb-1.5">Scope Target</label>
                  <input value={scopeTarget} onChange={e => setScopeTarget(e.target.value)} className="form-input" required />
                </div>
              )}
               <div className="subcard p-3 text-xs text-glass-400 space-y-2">
                  <p className="font-semibold text-white flex items-center gap-2"><HelpCircle className="w-4 h-4 text-accent-300" /> Permission Info</p>
                  {permissions.isSuperAdmin && <p>You have Super Admin permissions.</p>}
                  {permissions.isAdmin && !permissions.isSuperAdmin && <p>You have Admin permissions.</p>}
                  {permissions.isMod && !permissions.isAdmin && !permissions.isSuperAdmin && <p>You have Moderator permissions.</p>}
                  {permissions.isHelper && !permissions.isMod && !permissions.isAdmin && !permissions.isSuperAdmin && <p>As a Helper, you can only issue permanent cluster/fqdn bans, or global bans up to 1 month.</p>}
                  {!permissions.isSuperAdmin && <p>You do not have permission for `universal` scope.</p>}
              </div>
            </div>
          </div>

          <CaseLinkField
            className="mt-4"
            value={caseChoice}
            onChange={setCaseChoice}
            subject={playerUuid}
          />

          {error && (
            <div className="alert alert-danger mt-4" role="alert">
              <AlertCircle />
              <span>{error}</span>
            </div>
          )}

          {caseWarning && (
            <div className="alert alert-warning mt-4" role="alert">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              <span>{caseWarning}</span>
            </div>
          )}

          {linkedCase && (
            <div className="alert alert-success mt-4" role="status">
              <CheckCircle className="w-4 h-4 shrink-0" />
              <span>Attached to <span className="font-mono">{linkedCase}</span>.</span>
            </div>
          )}
        </div>
        
        <div className="modal-footer">
          <button type="button" onClick={onClose} className="glass-button-secondary">Cancel</button>
          <button type="submit" disabled={isSubmitting || isSuccess} className="btn-danger">
            {isSubmitting && <><Loader className="w-4 h-4 animate-spin" /> Submitting...</>}
            {isSuccess && <><CheckCircle className="w-4 h-4" /> Success!</>}
            {!isSubmitting && !isSuccess && <><Axe className="w-4 h-4" /> Submit Ban</>}
          </button>
        </div>
      </form>
    </div>
  );
};

// Add basic form-input styles to index.css if they don't exist
/*
@tailwind base;
@tailwind components;
@tailwind utilities;

@layer components {
  .form-input {
    @apply w-full p-2 bg-dark-900/50 rounded-md border border-glass-200/50 focus:ring-2 focus:ring-accent-400 focus:outline-none;
  }
  .form-checkbox {
    @apply h-4 w-4 rounded bg-dark-900/50 border-glass-200/50 text-accent-400 focus:ring-accent-400;
  }
}
*/
